// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { formatPersonName } from '@/account/utilities/format-person-name';
import { UsefulLifeRevision } from '@/asset/api/types';
import { MultiTimezoneTooltip } from '@/components/multi-timezone-tooltip';
import { SortDirection, invert } from '@/constants/sort';
import { USEFUL_LIFE_REVISION_REASON_LABELS } from '@/constants/useful-life-revision';
import { pluralize } from '@/utilities/pluralize';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';

interface Props {
  revisions: UsefulLifeRevision[];
}

type SortField =
  | 'effectiveDate'
  | 'usefulLifeMonths'
  | 'reason'
  | 'notes'
  | 'createdBy'
  | 'createdAt';

interface Column {
  align?: 'left' | 'right' | 'center' | 'justify' | 'inherit';
  field: SortField;
  label: string;
}

const columns: Column[] = [
  { field: 'effectiveDate', label: 'Date' },
  { field: 'usefulLifeMonths', label: 'Revised life', align: 'right' },
  { field: 'reason', label: 'Reason' },
  { field: 'notes', label: 'Notes' },
  { field: 'createdBy', label: 'Recorded by' },
  { field: 'createdAt', label: 'Recorded at' },
];

function getSortValue(revision: UsefulLifeRevision, field: SortField) {
  switch (field) {
    case 'effectiveDate':
      return revision.effectiveDate.toMillis();
    case 'usefulLifeMonths':
      return revision.usefulLifeMonths;
    case 'reason':
      return USEFUL_LIFE_REVISION_REASON_LABELS[revision.reason];
    case 'notes':
      return revision.notes ?? '';
    case 'createdBy':
      return formatPersonName(revision.createdBy);
    case 'createdAt':
      return revision.createdAt.toMillis();
  }
}

export function UsefulLifeRevisionTable(props: Props) {
  const [sortField, setSortField] = useState<SortField>('effectiveDate');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const sortedRevisions = useMemo(() => {
    const revisions = [...props.revisions];

    revisions.sort((left, right) => {
      const leftValue = getSortValue(left, sortField);
      const rightValue = getSortValue(right, sortField);

      let comparison = 0;
      if (typeof leftValue === 'number' && typeof rightValue === 'number') {
        comparison = leftValue - rightValue;
      } else {
        comparison = String(leftValue).localeCompare(String(rightValue));
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return revisions;
  }, [props.revisions, sortDirection, sortField]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(invert);
      return;
    }

    setSortField(field);
    setSortDirection(
      field === 'effectiveDate' || field === 'createdAt' ? 'desc' : 'asc'
    );
  };

  if (props.revisions.length === 0) {
    return (
      <Paper sx={{ px: 2, py: 3 }} variant="outlined">
        <Typography color="text.secondary" variant="body2">
          No useful life revisions have been recorded for this asset yet.
        </Typography>
      </Paper>
    );
  }

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small" sx={{ minWidth: 940 }}>
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableCell
                key={column.field}
                align={column.align}
                sortDirection={
                  sortField === column.field ? sortDirection : false
                }
              >
                <TableSortLabel
                  active={sortField === column.field}
                  direction={sortField === column.field ? sortDirection : 'asc'}
                  onClick={() => handleSort(column.field)}
                >
                  {column.label}
                </TableSortLabel>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedRevisions.map((revision) => (
            <TableRow key={revision.key}>
              <TableCell>
                {revision.effectiveDate.toFormat('yyyy-MM-dd')}
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  fontVariantNumeric: 'tabular-nums',
                  whiteSpace: 'nowrap',
                }}
              >
                {pluralize(revision.usefulLifeMonths, { singular: 'month' })}
              </TableCell>
              <TableCell>
                {USEFUL_LIFE_REVISION_REASON_LABELS[revision.reason]}
              </TableCell>
              <TableCell sx={{ maxWidth: 320 }}>
                {revision.notes ?? '—'}
              </TableCell>
              <TableCell>{formatPersonName(revision.createdBy)}</TableCell>
              <TableCell>
                <MultiTimezoneTooltip timestamp={revision.createdAt}>
                  <Typography
                    component="span"
                    variant="body2"
                    sx={{
                      textDecoration: 'underline',
                      textDecorationStyle: 'dotted',
                    }}
                  >
                    {revision.createdAt.toLocaleString({
                      dateStyle: 'medium',
                    })}
                  </Typography>
                </MultiTimezoneTooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
